/**
 * Platform-wide logged-in consent API — `/api/v1/customer-account/consent/*`.
 * Rollout fallback: tenant-scoped `/customer/consent/*` when platform routes 404.
 */
import type { ApiClient } from '../../shared/api/ApiClient.js';
import { ApiError } from '../../shared/api/ApiClient.js';
import { hasCustomerApiTenant } from '../../shared/tenant/customerApiTenant.js';
import { fetchPlatformAccountJson } from '../account/platformAccountFetch.js';
import type { MarketingAnalyticsPromptData } from './consentApi.js';
import type { ConsentPreferencesPayload } from './consentPreferencesPayload.js';

export const PLATFORM_CONSENT_PREFERENCES_PATH =
  '/api/v1/customer-account/consent/preferences';

export const PLATFORM_CONSENT_MARKETING_ANALYTICS_PROMPT_PATH =
  '/api/v1/customer-account/consent/marketing-analytics-prompt';

const TENANT_CONSENT_PREFERENCES_PATH = '/customer/consent/preferences';
const TENANT_CONSENT_MARKETING_ANALYTICS_PROMPT_PATH =
  '/customer/consent/marketing-analytics-prompt';

export interface ConsentApiRolloutFallback {
  readonly api: ApiClient;
  readonly fallbackTenantCode: string | null;
}

function tenantConsentFallbackSlug(
  rollout: ConsentApiRolloutFallback,
): string | null {
  const tenant = rollout.fallbackTenantCode?.trim() ?? '';
  if (tenant.length === 0 || !hasCustomerApiTenant(tenant)) {
    return null;
  }
  return tenant;
}

function canUseTenantConsentFallback(
  rollout: ConsentApiRolloutFallback | undefined,
): rollout is ConsentApiRolloutFallback {
  return rollout !== undefined && tenantConsentFallbackSlug(rollout) !== null;
}

function isPlatformConsentRouteMissing(err: unknown): boolean {
  return err instanceof ApiError && err.status === 404;
}

function normalizeMarketingAnalyticsPromptData(
  data: MarketingAnalyticsPromptData | undefined,
): MarketingAnalyticsPromptData {
  if (data === undefined) {
    return {
      needsPrompt: false,
      policyVersion: 'unknown',
      lastPromptedAt: null,
      deferAllowed: false,
      marketingGranted: false,
      analyticsGranted: false,
    };
  }
  return {
    ...data,
    marketingGranted: data.marketingGranted ?? false,
    analyticsGranted: data.analyticsGranted ?? false,
  };
}

export async function putPlatformConsentPreferences(input: {
  apiBaseUrl: string;
  body: ConsentPreferencesPayload;
  signal?: AbortSignal;
  rollout?: ConsentApiRolloutFallback;
}): Promise<void> {
  try {
    await fetchPlatformAccountJson({
      apiBaseUrl: input.apiBaseUrl,
      path: PLATFORM_CONSENT_PREFERENCES_PATH,
      method: 'PUT',
      body: input.body,
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
  } catch (err: unknown) {
    if (!isPlatformConsentRouteMissing(err) || !canUseTenantConsentFallback(input.rollout)) {
      throw err;
    }
    const fallbackTenant = tenantConsentFallbackSlug(input.rollout);
    if (fallbackTenant === null) {
      throw err;
    }
    await input.rollout.api.request(TENANT_CONSENT_PREFERENCES_PATH, {
      method: 'PUT',
      body: input.body,
      tenantCode: fallbackTenant,
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
  }
}

export async function fetchPlatformMarketingAnalyticsConsentPrompt(input: {
  apiBaseUrl: string;
  signal?: AbortSignal;
  rollout?: ConsentApiRolloutFallback;
}): Promise<MarketingAnalyticsPromptData> {
  try {
    const data = await fetchPlatformAccountJson<MarketingAnalyticsPromptData>({
      apiBaseUrl: input.apiBaseUrl,
      path: PLATFORM_CONSENT_MARKETING_ANALYTICS_PROMPT_PATH,
      method: 'GET',
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
    return normalizeMarketingAnalyticsPromptData(data);
  } catch (err: unknown) {
    if (!isPlatformConsentRouteMissing(err) || !canUseTenantConsentFallback(input.rollout)) {
      throw err;
    }
    const fallbackTenant = tenantConsentFallbackSlug(input.rollout);
    if (fallbackTenant === null) {
      throw err;
    }
    const data = await input.rollout.api.request<MarketingAnalyticsPromptData>(
      TENANT_CONSENT_MARKETING_ANALYTICS_PROMPT_PATH,
      {
        method: 'GET',
        tenantCode: fallbackTenant,
        ...(input.signal !== undefined ? { signal: input.signal } : {}),
      },
    );
    return normalizeMarketingAnalyticsPromptData(data);
  }
}

export async function postPlatformMarketingAnalyticsConsentPrompt(input: {
  apiBaseUrl: string;
  response: 'COMPLETED' | 'DEFERRED_LEGAL' | 'DISMISSED_ILLEGAL';
  signal?: AbortSignal;
  rollout?: ConsentApiRolloutFallback;
}): Promise<void> {
  try {
    await fetchPlatformAccountJson({
      apiBaseUrl: input.apiBaseUrl,
      path: PLATFORM_CONSENT_MARKETING_ANALYTICS_PROMPT_PATH,
      method: 'POST',
      body: { response: input.response },
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
  } catch (err: unknown) {
    if (!isPlatformConsentRouteMissing(err) || !canUseTenantConsentFallback(input.rollout)) {
      throw err;
    }
    const fallbackTenant = tenantConsentFallbackSlug(input.rollout);
    if (fallbackTenant === null) {
      throw err;
    }
    await input.rollout.api.request(TENANT_CONSENT_MARKETING_ANALYTICS_PROMPT_PATH, {
      method: 'POST',
      body: { response: input.response },
      tenantCode: fallbackTenant,
      ...(input.signal !== undefined ? { signal: input.signal } : {}),
    });
  }
}
